import { useContext, useEffect, useRef, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic "pill + floating panel" dropdown — a presentational shell.
 *
 * It owns nothing but its open/closed state : the trigger and the panel are
 * arbitrary rjBuild fragments rendered through `<View>`, so any project can
 * drop interactive children (facet lists, forms, menus…) inside and have
 * their `setData` / `submitData` actions work normally. Click-outside and
 * Escape close it. The panel is rendered inline (absolutely positioned under
 * the trigger), not portalled — simpler and robust inside scroll containers
 * (same approach as `NavDropdown`).
 *
 * YAML usage:
 *   - type: FloatingDropdown
 *     label: 'Marque'                 # simple text trigger, OR:
 *     trigger: [ ... rjBuild ... ]    # full custom trigger content
 *     active: ~~.selectedFilters.by_brand   # highlight when "set" (see below)
 *     align: left | right             # panel edge alignment (default left)
 *     panel:
 *       - type: FacetList
 *         ...
 *
 * `active` is coerced : a non-empty array / non-empty object / truthy non-empty
 * scalar all count as "active" (so an empty multi-select array reads inactive).
 *
 * ── Selection chips in the trigger (combobox style) ───────────────────────
 * Pass `selectionLocation` to display the *current selection of THIS filter*
 * as removable chips right inside the pill (each chip has an ✕ that clears
 * only that value). This is the per-filter alternative to a global
 * `ActiveFilterChips` bar.
 *
 *     selectionLocation: ~~.selectedFilters.by_content_type
 *     selectionType: list | scalar | tristate  # default: inferred from the value
 *                                     # 'tristate' reads {any,all,none} and shows
 *                                     # include chips + struck "≠ " exclude chips
 *     selectionLabelMap: ~~.statusLabels   # raw value → display label
 *     chipActions:                    # fired after a chip removal (refetch)
 *       - { what: setData, on: click, path: ~~.pagination.from, value: 0 }
 *       - { what: submitData, on: click, ... }
 *
 * When chips are shown, the active highlight (`activeClass`) is suppressed —
 * the chips themselves carry the "active" cue. Removal updates the store, then
 * the click bubbles to an inner `ActionDependant` that runs `chipActions` (the
 * refetch) and stops propagation, so the pill does NOT toggle open.
 *
 * `scrollToTop` (optional) — when a control *inside the panel* is clicked
 * (e.g. picking a year / a sort option), close the panel and scroll back so
 * the results are visible again. Pass a CSS selector to scroll that element to
 * the top of the viewport (e.g. `'#academie-search-bar'` to leave the sticky
 * filter bar in view), or `true` to scroll the window to the very top. Mirrors
 * `Pagination`'s `scrollToTop`. Only set it on *single-select* dropdowns — a
 * multi-select facet would close after the first toggle.
 */

const DEFAULT_TRIGGER_CLASS =
  'flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all whitespace-nowrap border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600 bg-white'
const DEFAULT_ACTIVE_CLASS = 'bg-indigo-600 border-indigo-600 text-white hover:text-white'
const DEFAULT_PANEL_CLASS =
  'absolute top-full mt-1.5 z-40 min-w-[14rem] bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden'
const DEFAULT_CHIP_CLASS =
  'inline-flex items-center gap-1 text-xs font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors cursor-pointer'
// Excluded (NOT) tri-state chip : rose + struck-through, distinct from include.
const DEFAULT_EXCLUDE_CHIP_CLASS =
  'inline-flex items-center gap-1 text-xs font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors cursor-pointer line-through'

const isActiveValue = (v) => {
  if (Array.isArray(v)) return v.length > 0
  if (v && typeof v === 'object') {
    // A tri-state model {any,all,none} is active only if a bucket is non-empty
    // (an all-empty object must read inactive). Falls back to "has any key" for
    // other object shapes (e.g. a range {gte}).
    const keys = Object.keys(v)
    const triKeys = ['any', 'all', 'none']
    if (keys.length && keys.every((k) => triKeys.includes(k))) {
      return triKeys.some((k) => Array.isArray(v[k]) && v[k].length > 0)
    }
    return keys.length > 0
  }
  return v !== '' && v != null && v !== false
}

export const FloatingDropdown = ({ props, path, datafield, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const label = resolve(props.label)
  const active = isActiveValue(resolve(props.active))
  const align = resolve(props.align) === 'right' ? 'right' : 'left'
  const triggerClass = resolve(props.triggerClass) ?? DEFAULT_TRIGGER_CLASS
  const activeClass = resolve(props.activeClass) ?? DEFAULT_ACTIVE_CLASS
  const panelClass = resolve(props.panelClass) ?? DEFAULT_PANEL_CLASS
  const chipClass = resolve(props.chipClass) ?? DEFAULT_CHIP_CLASS
  const excludeChipClass = resolve(props.excludeChipClass) ?? DEFAULT_EXCLUDE_CHIP_CLASS
  const scrollToTop = resolve(props.scrollToTop)

  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const chipRef = useRef(null)

  // ── Selection chips (combobox style) ──────────────────────────────────
  // When `selectionLocation` is set, read this filter's own selection and
  // render one removable chip per value inside the trigger.
  const hasSelection = props.selectionLocation != null
  const selectionLabelMap = resolve(props.selectionLabelMap) || {}
  let chips = []
  if (hasSelection) {
    const { formData, formDataPath } = propsDataLocationToPathAndValue({
      currentPath: path,
      datafield,
      dataLocation: props.selectionLocation,
      defaultValue: '',
      globalDataContext,
      templateContext,
    })
    const type = resolve(props.selectionType) || (Array.isArray(formData) ? 'list' : 'scalar')
    const mapLabel = (v) => (selectionLabelMap[v] != null ? String(selectionLabelMap[v]) : String(v))
    if (type === 'tristate') {
      // Backend `{any,all,none}` shape : one chip per included value (any+all),
      // one struck-through "≠ " chip per excluded value (none). Removing drops
      // the value from its bucket only.
      const model = formData && typeof formData === 'object' && !Array.isArray(formData) ? formData : {}
      const listAt = (k) => (Array.isArray(model[k]) ? model[k] : [])
      const removeFrom = (bucket, item) =>
        globalDataContext.updateData({ ...model, [bucket]: listAt(bucket).filter((x) => x !== item) }, formDataPath)
      const incChips = ['any', 'all'].flatMap((bucket) =>
        listAt(bucket).map((item) => ({
          id: `inc:${item}`,
          label: mapLabel(item),
          remove: () => removeFrom(bucket, item),
        })),
      )
      const excChips = listAt('none').map((item) => ({
        id: `exc:${item}`,
        label: `≠ ${mapLabel(item)}`,
        excluded: true,
        remove: () => removeFrom('none', item),
      }))
      chips = [...incChips, ...excChips]
    } else if (type === 'list' && Array.isArray(formData)) {
      chips = formData.map((item) => ({
        id: String(item),
        label: mapLabel(item),
        remove: () => globalDataContext.updateData(formData.filter((x) => x !== item), formDataPath),
      }))
    } else if (type !== 'list' && formData != null && formData !== '' && formData !== false) {
      chips = [{
        id: String(formData),
        label: mapLabel(formData),
        remove: () => globalDataContext.updateData('', formDataPath),
      }]
    }
  }

  // Active highlight is suppressed once chips are shown — they carry the cue.
  const applyActiveClass = active && chips.length === 0

  const onTriggerClick = (e) => {
    // A click that originated on a chip (its ✕) must not toggle the panel.
    if (e.target.closest('[data-chip-remove]')) return
    setOpen((o) => !o)
  }

  // After an in-panel selection (a click on any button), close the panel and
  // scroll the results back into view. Deferred to the next tick so the
  // selected option's own setData/submitData actions dispatch first (closing
  // the panel unmounts that subtree).
  const handlePanelClick = (e) => {
    if (!scrollToTop || !e.target.closest('button')) return
    setTimeout(() => {
      setOpen(false)
      if (typeof window === 'undefined') return
      if (typeof scrollToTop === 'string') {
        document.querySelector(scrollToTop)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }, 0)
  }

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <ActionDependant {...props}>
      <div ref={ref} className="relative shrink-0">
        <button
          type="button"
          onClick={onTriggerClick}
          aria-haspopup="true"
          aria-expanded={open}
          className={`${triggerClass} ${applyActiveClass ? activeClass : ''}`}
        >
          {props.trigger != null ? (
            <View props={props.trigger} path={`${path}.trigger`} datafield="trigger" currentData={currentData?.trigger} />
          ) : (
            <span>{label}</span>
          )}

          {chips.length > 0 && (
            <ActionDependant actions={props.chipActions} attributesHolderRef={chipRef}>
              <span ref={chipRef} className="inline-flex items-center gap-1 flex-wrap">
                {chips.map((chip) => (
                  <span
                    key={chip.id}
                    data-chip-remove
                    title={chip.excluded ? 'Retirer cette exclusion' : 'Retirer ce filtre'}
                    onClick={chip.remove}
                    className={chip.excluded ? excludeChipClass : chipClass}
                  >
                    {chip.label}
                    <X className="w-3 h-3" />
                  </span>
                ))}
              </span>
            </ActionDependant>
          )}

          <ChevronDown size={13} className={`opacity-60 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className={`${panelClass} ${align === 'right' ? 'right-0' : 'left-0'}`} onClick={handlePanelClick}>
            <View props={props.panel} path={`${path}.panel`} datafield="panel" currentData={currentData?.panel} />
          </div>
        )}
      </div>
    </ActionDependant>
  )
}
